import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Button, Stack, CircularProgress, IconButton, Typography, Tooltip, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import { ElectricCar as ElectricCarIcon } from '@mui/icons-material';
import { convertDateToText } from '../utils';
import { fetchAvailableSpots } from '../api/bookings';

function BookingsActions({
  t,
  availableDates,
  handleBookSlot,
  isDateBooked,
  userInfo,
  handleToggleElectricCar,
  loadingBookings,
  onSlotsDataUpdate
}) {
  const [availableSpots, setAvailableSpots] = useState({});
  const [selectedSpots, setSelectedSpots] = useState({});
  const [loadingSpots, setLoadingSpots] = useState({});
  const [blockedByAssigned, setBlockedByAssigned] = useState({});
  const [chargerSlotsFiltered, setChargerSlotsFiltered] = useState({});
  const fetchedDatesRef = useRef(new Set());

  // Function to fetch spots for a specific date
  const fetchSpotsForDate = useCallback(async (date) => {
    if (!date || fetchedDatesRef.current.has(date)) return;

    fetchedDatesRef.current.add(date);

    try {
      setLoadingSpots(prev => ({ ...prev, [date]: true }));
      const data = await fetchAvailableSpots(date);
      setAvailableSpots(prev => ({ ...prev, [date]: data.available_spots }));
      setBlockedByAssigned(prev => ({ ...prev, [date]: !!data.blocked_by_assigned_slot }));
      setChargerSlotsFiltered(prev => ({ ...prev, [date]: data.charger_slots_filtered || false }));

      // Pass the slots data to parent component for charger modal logic
      if (onSlotsDataUpdate) {
        onSlotsDataUpdate(date, data.available_spots || []);
      }

      // Set default selection to first available spot (excluding charger slots if filtered)
      const filteredSpots = (data.available_spots || []).filter(slot => !data.charger_slots_filtered || !slot.has_charger);
      if (filteredSpots.length > 0) {
        setSelectedSpots(prev => ({ ...prev, [date]: filteredSpots[0].id }));
      }
    } catch (error) {
      console.error(`Error fetching spots for ${date}:`, error);
      setAvailableSpots(prev => ({ ...prev, [date]: [] }));
      setBlockedByAssigned(prev => ({ ...prev, [date]: false }));
      setChargerSlotsFiltered(prev => ({ ...prev, [date]: false }));
    } finally {
      setLoadingSpots(prev => ({ ...prev, [date]: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSlotsDataUpdate]); // Include onSlotsDataUpdate but disable the warning for other deps

  // Fetch available spots for both dates only when the dates change
  useEffect(() => {
    if (availableDates.next_day) {
      fetchSpotsForDate(availableDates.next_day);
    }
    if (availableDates.second_day) {
      fetchSpotsForDate(availableDates.second_day);
    }
  }, [availableDates.next_day, availableDates.second_day, fetchSpotsForDate]); // Include fetchSpotsForDate

  const handleSpotChange = useCallback((date, spotId) => {
    setSelectedSpots(prev => ({ ...prev, [date]: spotId }));
  }, []);

  const handleBookSlotWithSpot = useCallback((date) => {
    const selectedSpotId = selectedSpots[date];
    handleBookSlot(date, selectedSpotId);
  }, [selectedSpots, handleBookSlot]);

  const renderBookingRow = useCallback((date, dateKey) => {
    const allSpots = availableSpots[date] || [];
    const isLoading = loadingSpots[date];
    const isBooked = isDateBooked(date);
    const isBlocked = blockedByAssigned[date];
    const selectedSpot = selectedSpots[date];
    const chargerFiltered = chargerSlotsFiltered[date];

    // Filter out charger slots if user has reached the limit
    const spots = allSpots.filter(slot => !chargerFiltered || !slot.has_charger);

    // Hide the entire row if user already has a booking for this date or is blocked by assigned slot
    if (isBooked || isBlocked) {
      return null;
    }

    return (
      <Stack direction="row" spacing={1} alignItems="center" key={dateKey}>
        <FormControl size="small" sx={{ flex: 1 }}>
          <InputLabel>{t('bookings.spot')}</InputLabel>
          <Select
            value={selectedSpot || ''}
            label={t('bookings.spot')}
            onChange={(e) => handleSpotChange(date, e.target.value)}
            disabled={!date || isLoading || allSpots.length === 0}
          >
            {spots.map((spot) => (
              <MenuItem key={spot.id} value={spot.id}>
                {spot.slot_number} {spot.has_charger ? '⚡' : ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          onClick={() => handleBookSlotWithSpot(date)}
          disabled={!date || isLoading || !selectedSpot || spots.length === 0}
          sx={{ flex: 1 }}
        >
          {date
            ? t('actions.create_booking') + ' ' + convertDateToText(date, t)
            : 'Loading...'}
        </Button>
      </Stack>
    );
  }, [availableSpots, loadingSpots, isDateBooked, blockedByAssigned, selectedSpots, chargerSlotsFiltered, t, handleBookSlotWithSpot, handleSpotChange]);

  return (
    <Box>
      <Stack spacing={2}>
        {renderBookingRow(availableDates.next_day, 'next_day')}
        {renderBookingRow(availableDates.second_day, 'second_day')}
        
        <Stack direction="row" alignItems="center" spacing={1}>
          <Tooltip title={t('user.toggle_electric_car')} arrow>
            <IconButton
              onClick={handleToggleElectricCar}
              aria-label={t('user.toggle_electric_car')}
              sx={{
                color: userInfo?.has_electric_car ? 'primary.main' : 'grey.300'
              }}
            >
              <ElectricCarIcon />
            </IconButton>
          </Tooltip>
          <Typography>{t('user.electric_car')}</Typography>
        </Stack>
        {loadingBookings && <CircularProgress />}
      </Stack>
    </Box>
  );
}

export default BookingsActions;
